import { store } from '@store/Store'
import type { SoundTone } from '@/api/workspaceSettingsApi'
import { showBrowserNotification } from '@utils/browserNotifications'
import { t } from '@store/I18nStore'
import {
  normalizeSoundTone,
  playNotificationSound,
  type SoundEventKey,
} from '@utils/notificationSounds'

function getSoundTone(eventKey: SoundEventKey): SoundTone {
  const settings = store.operatorWorkspaceSettings.soundNotifications
  return normalizeSoundTone(settings.events[eventKey], 'sound-1')
}

function shouldPlaySound(): boolean {
  return store.operatorWorkspaceSettings.soundNotifications.enabled
}

/** Sound for a new visitor appeal in an unaccepted (inbox) dialog. */
export function notifyNewConversation(options?: { chatId?: string; title?: string; preview?: string }) {
  if (shouldPlaySound()) {
    playNotificationSound('new_conversation', getSoundTone('new_conversation'))
  }

  showBrowserNotification({
    title: 'ChatX',
    body: options?.preview
      ? `${t('chat.new_request')}\n${options.title ?? t('chat.dialog.awaiting_reply')}\n${options.preview}`
      : `${t('chat.new_request')}\n${options?.title ?? t('chat.dialog.awaiting_reply')}`,
    chatId: options?.chatId,
  })
}

/** Sound for a visitor appeal in an accepted (my) dialog. */
export function notifyAcceptedDialogMessage(options: {
  chatId: string
  visitorName: string
  messageText: string
}) {
  if (shouldPlaySound()) {
    playNotificationSound('accepted_dialog', getSoundTone('accepted_dialog'))
  }

  const preview = options.messageText.trim()
  const inDialog = t('chat.status.in_dialog')
  showBrowserNotification({
    title: 'ChatX',
    body: preview
      ? `${inDialog}\n\n${options.visitorName}\n${preview}`
      : `${inDialog}\n\n${options.visitorName}`,
    chatId: options.chatId,
  })
}

/** Sound when a dialog is being transferred to this operator. */
export function notifyDialogTransfer(options?: {
  chatId?: string
  title?: string
  fromOperatorName?: string
  preview?: string
}) {
  if (shouldPlaySound()) {
    playNotificationSound('dialog_transfer', getSoundTone('dialog_transfer'))
  }

  const from = options?.fromOperatorName?.trim()
  const title = options?.title?.trim() || t('chat.dialog_title')
  const preview = options?.preview?.trim()
  const transferTitle = t('chat.transfer.title')
  const incomingFrom = from
    ? t('chat.transfer.incoming_from', { operator: from }) + ' «' + title + '»'
    : t('chat.transfer.incoming_body') + ' «' + title + '»'
  showBrowserNotification({
    title: 'ChatX',
    body: preview
      ? `${transferTitle}\n${from ? `${from} → ${t('chat.you')}` : title}\n${preview}`
      : incomingFrom,
    chatId: options?.chatId,
  })
}

/** @deprecated Use notifyAcceptedDialogMessage */
export const notifyNewMessage = notifyAcceptedDialogMessage
